import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import TextArea from '@atlaskit/textarea'
import Select from '@atlaskit/select'
import { updateLibraryScenarioAsync } from '../../store/slices/librarySlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * EDIT LIBRARY SCENARIO MODAL
 *
 * Modal dialog for editing a library test scenario.
 */
function EditLibraryScenarioModal({
  isOpen,
  onClose,
  scenario
}) {
  const dispatch = useDispatch()
  const { showError, showWarning } = useNotification()

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState({ value: 'Medium', label: 'Medium' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Priority options
  const priorityOptions = [
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' }
  ]

  // Initialize form with scenario data when modal opens
  useEffect(() => {
    if (scenario) {
      setName(scenario.scenarioName || '')
      setDescription(scenario.description || '')
      const priorityValue = scenario.priority || 'Medium'
      setPriority({ value: priorityValue, label: priorityValue })
    }
  }, [scenario])

  // Handle form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      showWarning('Validation Error', 'Please enter a scenario name')
      return
    }

    setIsSubmitting(true)

    try {
      await dispatch(updateLibraryScenarioAsync({
        scenarioId: scenario.id,
        updates: {
          scenarioName: name.trim(),
          description: description.trim(),
          priority: priority.value
        }
      })).unwrap()

      onClose()
    } catch (error) {
      console.error('Failed to update library scenario:', error)
      const errorMessage = error?.message || 'Failed to update scenario. Please try again.'
      showError('Update Scenario Failed', errorMessage)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    onClose()
  }

  if (!scenario) return null

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '480px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)'
            }}
          >
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Edit Library Scenario
            </Dialog.Title>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            {/* Scenario Name */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Scenario Name *
              </label>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim() && !isSubmitting) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                placeholder="Enter scenario name"
                isRequired
                autoFocus
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Description (optional)
              </label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this template tests"
                minimumRows={2}
                maxHeight="100px"
              />
            </div>

            {/* Priority */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Priority
              </label>
              <Select
                options={priorityOptions}
                value={priority}
                onChange={setPriority}
                placeholder="Select priority..."
                styles={{
                  control: (base) => ({
                    ...base,
                    fontSize: '14px'
                  })
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!name.trim()}
            >
              Save Changes
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { EditLibraryScenarioModal }
